import React, { MouseEvent, ReactNode } from "react";
import styled, { css } from "styled-components";
import { ChevronDown } from "lucide-react";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens, NEW_LOOK_ON_BRAND } from "common/NewLook/tokens.ts";
import { hexToRGB } from "@shared/utils/misc";
import {
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_GUTTER,
  NEW_LOOK_PHONE_HIT,
} from "common/NewLook/layout.ts";
import { CHART_RANGE_ID } from "testIds";

const RANGE_PAD = 10;

type PhoneHeroSurface = "brand" | "panel";

interface PhoneHeroRangesProps<T extends string> {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  surface?: PhoneHeroSurface;
  testId?: string;
  getLabel?: (option: T) => string;
}

interface PhoneHeroCollapseProps {
  open: boolean;
  children: ReactNode;
}

interface PhoneHeroToggleProps {
  open: boolean;
  onToggle: () => void;
  testId?: string;
  surface?: PhoneHeroSurface;
}

export const PhoneHeroRanges = <T extends string>({
  options,
  value,
  onChange,
  label,
  surface = "brand",
  testId,
  getLabel = toRangeLabel,
}: PhoneHeroRangesProps<T>) => (
  <Ranges
    role="group"
    aria-label={label}
    $surface={surface}
    data-testid={testId}
  >
    {options.map((option) => (
      <Range
        key={option}
        type="button"
        $on={option === value}
        $surface={surface}
        aria-pressed={option === value}
        data-testid={CHART_RANGE_ID}
        onClick={() => onChange(option)}
      >
        {getLabel(option)}
      </Range>
    ))}
  </Ranges>
);

const toRangeLabel = (option: string) => option.toUpperCase();

export const PhoneHeroToggle = ({
  open,
  onToggle,
  testId,
  surface = "brand",
}: PhoneHeroToggleProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  return (
    <Toggle
      type="button"
      $surface={surface}
      aria-expanded={open}
      onClick={(event: MouseEvent) => {
        event.stopPropagation();
        onToggle();
      }}
      data-testid={testId}
    >
      <ToggleLabel>
        {open ? t("overview.hide_chart") : t("overview.show_chart")}
      </ToggleLabel>
      <ToggleIcon $open={open} size={14} strokeWidth={2} />
    </Toggle>
  );
};

export const PhoneHeroCollapse = ({
  open,
  children,
}: PhoneHeroCollapseProps) => (
  <Collapse $open={open} aria-hidden={!open} inert={!open}>
    <CollapseInner>{children}</CollapseInner>
  </Collapse>
);

const phoneHit = css`
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: ${NEW_LOOK_PHONE_HIT}px;
    transform: translateY(-50%);
  }
`;
const Ranges = styled.div<{ $surface: PhoneHeroSurface }>`
  display: flex;
  justify-content: space-between;
  gap: 4px;
  padding: 12px ${NEW_LOOK_PHONE_GUTTER}px 0;
  ${(p) =>
    p.$surface === "panel" &&
    css`
      padding: 12px 0 0;
      margin: 0 -${RANGE_PAD}px -8px;
    `}
`;
const Range = styled.button<{ $on: boolean; $surface: PhoneHeroSurface }>`
  min-width: ${NEW_LOOK_PHONE_CONTROL}px;
  height: ${NEW_LOOK_PHONE_CONTROL}px;
  border: 0;
  padding: 0 ${RANGE_PAD}px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  ${phoneHit};
  color: ${(p) =>
    p.$on ? NEW_LOOK_ON_BRAND.text : NEW_LOOK_ON_BRAND.secondary};
  background: ${(p) => (p.$on ? NEW_LOOK_ON_BRAND.well : "transparent")};
  ${(p) => p.$surface === "panel" && panelRange(p.$on)}
`;
const panelRange = (on: boolean) => css`
  color: ${(p) =>
    on
      ? getNewLookTokens(p.theme).accent
      : getNewLookTokens(p.theme).secondary};
  background: ${(p) =>
    on
      ? hexToRGB(
          getNewLookTokens(p.theme).accent,
          p.theme.isDarkMode ? 0.18 : 0.08
        )
      : "transparent"};
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: -2px;
  }
`;
const Toggle = styled.button<{ $surface: PhoneHeroSurface }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: ${NEW_LOOK_PHONE_CONTROL}px;
  padding: 0 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${NEW_LOOK_ON_BRAND.secondary};
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  ${phoneHit};
  &:focus-visible {
    outline: 2px solid ${NEW_LOOK_ON_BRAND.secondary};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$surface === "panel" &&
    css`
      color: ${getNewLookTokens(p.theme).accent};
      &:focus-visible {
        outline-color: ${getNewLookTokens(p.theme).focus};
      }
    `}
`;
const ToggleLabel = styled.span`
  @media (max-width: 359.98px) {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;
const ToggleIcon = styled(ChevronDown)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$open ? 180 : 0)}deg);
`;
const Collapse = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.25s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const CollapseInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;
